import * as React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, Users, UserCheck, Mailbox, Search, Upload, ShieldCheck } from 'lucide-react';
import { PageHeader } from '../../../design-system/components/PageHeader';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../../components/ui/select';
import { useApi } from '../../../design-system/useApi';
import { formatDate } from '../../../design-system/format';
import { VISUAL, statusPill, statusDot } from '../../../design-system/visual';
import { InviteEmployeeDialog } from '../components/InviteEmployeeDialog';
import { ImportCsvDialog } from '../../invoicing/components/ImportCsvDialog';
import { hrApi } from '../api';
import { usePermission } from '../../../core/auth/usePermission';
import type { EmployeeWithUser } from '../types';

const EMPLOYEE_IMPORT_FIELDS = [
  { key: 'email', label: 'Email', required: true },
  { key: 'name', label: 'Name', required: true },
  { key: 'employee_no', label: 'Employee #' },
  { key: 'department', label: 'Department' },
  { key: 'designation', label: 'Designation' },
  { key: 'hire_date', label: 'Hire date' },
  { key: 'scheduled_clock_in', label: 'Clock-in' },
  { key: 'scheduled_clock_out', label: 'Clock-out' },
  { key: 'hourly_rate', label: 'Hourly rate' },
  // Salaried pay: payroll prefers this over the hourly rate when both are present.
  { key: 'monthly_base_salary', label: 'Monthly salary' },
  { key: 'termination_date', label: 'Last working day' },
];

/** Stable per-name pastel — picks a hue from name hash so the same person
 *  always gets the same avatar tint across renders. */
function avatarBgFor(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) % 360;
  // Use a low-saturation pastel — desaturated per the design-taste rules.
  return `oklch(0.86 0.08 ${h})`;
}

function initialsFor(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function TeamPage() {
  const { data, loading, reload } = useApi<{ employees: EmployeeWithUser[] }>('/api/hr/employees');
  const canManageAccess = usePermission('access.manage');
  const [inviteOpen, setInviteOpen] = React.useState(false);
  const [importOpen, setImportOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [filter, setFilter] = React.useState<'all' | 'active' | 'pending' | 'inactive'>('all');
  const [dept, setDept] = React.useState<string>('all');
  const [sort, setSort] = React.useState<'name' | 'department' | 'hired'>('name');

  const employees = data?.employees ?? [];
  const departments = React.useMemo(
    () => Array.from(new Set(employees.map((e) => e.department).filter((d): d is string => !!d))).sort(),
    [employees],
  );
  const active = employees.filter((e) => e.is_active !== false && e.has_password).length;
  const pending = employees.filter((e) => !e.has_password).length;
  const inactive = employees.filter((e) => e.is_active === false).length;

  const filtered = React.useMemo(() => {
    let rows = employees;
    if (filter === 'active') rows = rows.filter((e) => e.is_active !== false && e.has_password);
    if (filter === 'pending') rows = rows.filter((e) => !e.has_password);
    if (filter === 'inactive') rows = rows.filter((e) => e.is_active === false);
    if (dept !== 'all') rows = rows.filter((e) => (e.department ?? '') === dept);
    const q = search.trim().toLowerCase();
    if (q) rows = rows.filter((e) =>
      e.name?.toLowerCase().includes(q) ||
      e.email?.toLowerCase().includes(q) ||
      e.department?.toLowerCase().includes(q),
    );
    const sorted = [...rows];
    sorted.sort((a, b) => {
      if (sort === 'department') return (a.department ?? '').localeCompare(b.department ?? '') || (a.name ?? '').localeCompare(b.name ?? '');
      if (sort === 'hired') return (b.hire_date ?? '').localeCompare(a.hire_date ?? '');
      return (a.name ?? '').localeCompare(b.name ?? '');
    });
    return sorted;
  }, [employees, search, filter, dept, sort]);

  return (
    <div>
      <PageHeader
        eyebrow="Team / People"
        title="Your people"
        description="Everyone on the payroll, on attendance, and on your projects."
        actions={
          <div className="flex items-center gap-2">
            {canManageAccess && (
              <Button
                variant="outline"
                asChild
                className="rounded-full h-10 px-4 text-[13px] font-semibold gap-1.5"
              >
                <Link to="/dashboard/settings/access">
                  <ShieldCheck className="h-3.5 w-3.5" />
                  Roles &amp; access
                </Link>
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => setImportOpen(true)}
              className="rounded-full h-10 px-4 text-[13px] font-semibold gap-1.5"
            >
              <Upload className="h-3.5 w-3.5" />
              Import CSV
            </Button>
            <Button
              onClick={() => setInviteOpen(true)}
              className="rounded-full h-10 px-4 text-[13px] font-semibold gap-1.5"
            >
              <Plus className="h-3.5 w-3.5" />
              Invite employee
            </Button>
          </div>
        }
      />

      <div className="px-6 sm:px-8 lg:px-10 pb-12 space-y-6">
        {/* KPI strip */}
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Kpi label="Total" value={employees.length} caption="On the team" icon={<Users className="h-3.5 w-3.5" />} />
          <Kpi label="Active" value={active} caption="Signed in" icon={<UserCheck className="h-3.5 w-3.5" />} tone="success" />
          <Kpi label="Pending" value={pending} caption="Invite sent" icon={<Mailbox className="h-3.5 w-3.5" />} tone={pending > 0 ? 'saffron' : 'muted'} />
          <Kpi label="Inactive" value={inactive} caption="Disabled" icon={<Users className="h-3.5 w-3.5" />} tone="muted" />
        </section>

        {/* Search + filter */}
        <section className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="h-3.5 w-3.5 text-muted-foreground absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            <Input
              type="search"
              placeholder="Search name, email, department…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-10 pl-9 rounded-full bg-card/80 border-border/60 text-[13px]"
            />
          </div>
          <div className="inline-flex items-center gap-1 p-1 rounded-full bg-muted/50 ring-1 ring-border/40">
            {(['all', 'active', 'pending', 'inactive'] as const).map((k) => (
              <FilterPill key={k} active={filter === k} onClick={() => setFilter(k)}>
                {k === 'all' ? 'All' : k[0].toUpperCase() + k.slice(1)}
              </FilterPill>
            ))}
          </div>
          {departments.length > 0 && (
            <Select value={dept} onValueChange={setDept}>
              <SelectTrigger className="h-10 rounded-full bg-card/80 border-border/60 px-3.5 text-[12.5px] font-semibold w-auto min-w-[150px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All departments</SelectItem>
                {departments.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Select value={sort} onValueChange={(v) => setSort(v as 'name' | 'department' | 'hired')}>
            <SelectTrigger className="h-10 rounded-full bg-card/80 border-border/60 px-3.5 text-[12.5px] font-semibold w-auto min-w-[130px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="name">Sort: Name</SelectItem>
              <SelectItem value="department">Sort: Department</SelectItem>
              <SelectItem value="hired">Sort: Newest hire</SelectItem>
            </SelectContent>
          </Select>
        </section>

        {/* Cards grid */}
        {loading ? (
          <SkeletonGrid />
        ) : filtered.length === 0 ? (
          <EmptyTeam total={employees.length} onInvite={() => setInviteOpen(true)} />
        ) : (
          <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filtered.map((emp, i) => (
              <motion.div
                key={emp.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ type: 'spring', stiffness: 160, damping: 22, delay: Math.min(i * 0.025, 0.2) }}
                whileHover={{ y: -3 }}
              >
                <Link
                  to={`/dashboard/hr/team/${emp.id}`}
                  className={[VISUAL.bento, 'block p-5 will-change-transform transition-shadow hover:shadow-[0_24px_48px_-16px_oklch(0_0_0/0.1)]'].join(' ')}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className="h-12 w-12 rounded-2xl ring-1 ring-border/40 flex items-center justify-center text-[13px] font-bold text-foreground/85 shrink-0"
                      style={{ background: avatarBgFor(emp.name ?? emp.email ?? '?') }}
                      aria-hidden
                    >
                      {initialsFor(emp.name ?? emp.email ?? '?')}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[14px] font-semibold tracking-[-0.005em] text-foreground truncate">
                        {emp.name ?? '(name pending)'}
                      </p>
                      <p className="text-[12px] text-muted-foreground truncate">{emp.email}</p>
                    </div>
                  </div>
                  <div className="mt-4 grid grid-cols-2 gap-2.5 text-[12px]">
                    <div>
                      <p className="text-muted-foreground/80 uppercase tracking-[0.12em] text-[10px] font-semibold">Dept</p>
                      <p className="text-foreground font-medium mt-0.5 truncate">{emp.department ?? '—'}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground/80 uppercase tracking-[0.12em] text-[10px] font-semibold">Hired</p>
                      <p className="text-foreground font-medium mt-0.5 truncate">{emp.hire_date ? formatDate(emp.hire_date) : '—'}</p>
                    </div>
                  </div>
                  <div className="mt-4 pt-3 border-t border-border/40">
                    {!emp.has_password ? (
                      <span className={statusPill('warning')}><span className={statusDot('warning')} />Invite pending</span>
                    ) : !emp.is_active ? (
                      <span className={statusPill('destructive')}><span className={statusDot('destructive')} />Inactive</span>
                    ) : (
                      <span className={statusPill('success')}><span className={statusDot('success')} />Active</span>
                    )}
                  </div>
                </Link>
              </motion.div>
            ))}
          </section>
        )}
      </div>

      <InviteEmployeeDialog
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        onInvited={() => { setInviteOpen(false); reload(); }}
      />

      <ImportCsvDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        title="Import employees from CSV"
        description="Upload a CSV with a header row. Each valid row is invited — they'll get a magic-link email to set up their account."
        fields={EMPLOYEE_IMPORT_FIELDS}
        onImport={(rows) => hrApi.importEmployees(rows)}
        onImported={reload}
      />
    </div>
  );
}

function Kpi({
  label, value, caption, icon, tone = 'neutral',
}: { label: string; value: number; caption: string; icon: React.ReactNode; tone?: 'neutral' | 'success' | 'saffron' | 'muted' }) {
  const toneClass =
    tone === 'success' ? 'bg-success/10 text-success ring-success/25' :
    tone === 'saffron' ? 'bg-saffron/15 text-saffron-foreground ring-saffron/30' :
    tone === 'muted'   ? 'bg-muted text-muted-foreground ring-border/50' :
                         'bg-muted text-muted-foreground ring-border/50';
  return (
    <div className={[VISUAL.bento, 'p-5'].join(' ')}>
      <div className="flex items-center justify-between mb-3">
        <p className={VISUAL.eyebrow}>{label}</p>
        <span className={['inline-flex h-8 w-8 items-center justify-center rounded-lg ring-1', toneClass].join(' ')}>{icon}</span>
      </div>
      <p className="font-sans font-light text-[2.2rem] leading-none tracking-[-0.03em] tabular-nums text-foreground">{value}</p>
      <p className="text-[12px] text-muted-foreground mt-2">{caption}</p>
    </div>
  );
}

function FilterPill({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'inline-flex items-center px-3.5 h-8 text-[12px] font-semibold tracking-[-0.005em] rounded-full transition-colors',
        active ? 'bg-foreground text-background' : 'text-muted-foreground hover:text-foreground',
      ].join(' ')}
    >
      {children}
    </button>
  );
}

function SkeletonGrid() {
  return (
    <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className={[VISUAL.bento, 'p-5'].join(' ')}>
          <div className="flex items-start gap-3">
            <div className="h-12 w-12 rounded-2xl bg-muted/60 animate-pulse" />
            <div className="flex-1 space-y-1.5">
              <div className="h-3 w-2/3 rounded bg-muted/60 animate-pulse" />
              <div className="h-2.5 w-1/2 rounded bg-muted/40 animate-pulse" />
            </div>
          </div>
          <div className="mt-5 grid grid-cols-2 gap-2">
            <div className="h-7 rounded bg-muted/40 animate-pulse" />
            <div className="h-7 rounded bg-muted/40 animate-pulse" />
          </div>
        </div>
      ))}
    </section>
  );
}

function EmptyTeam({ total, onInvite }: { total: number; onInvite: () => void }) {
  if (total === 0) {
    return (
      <div className={[VISUAL.bento, 'py-14 px-8 text-center max-w-md mx-auto'].join(' ')}>
        <div className="mx-auto mb-5 h-14 w-14 rounded-2xl bg-saffron/15 ring-1 ring-saffron/25 flex items-center justify-center">
          <Users className="h-6 w-6 text-saffron-foreground" strokeWidth={1.6} />
        </div>
        <p className="text-[18px] font-semibold tracking-[-0.01em] text-foreground">Build your team</p>
        <p className="text-[13.5px] text-muted-foreground mt-1.5 leading-relaxed">
          Invite your first employee — they'll get a magic link to set up their account and show up here.
        </p>
        <div className="mt-6">
          <Button onClick={onInvite} className="rounded-full h-10 px-5 font-semibold gap-1.5">
            <Plus className="h-3.5 w-3.5" /> Invite employee
          </Button>
        </div>
      </div>
    );
  }
  return (
    <div className={[VISUAL.bento, 'py-12 px-8 text-center'].join(' ')}>
      <p className="text-[14px] font-semibold text-foreground">No matches</p>
      <p className="text-[13px] text-muted-foreground mt-1">Clear the search or pick a different filter.</p>
    </div>
  );
}
